import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import {
    FormControl,
    type FormControlProps,
    FormHelperText,
    type FormHelperTextProps,
    IconButton,
    InputLabel,
    type InputLabelProps,
    MenuItem,
    Select,
    type SelectProps,
    Typography,
} from '@mui/material';

import useId from '@mui/utils/useId';
import { type ComponentProps, createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

/** Stable context so IconComponent stays referentially stable across renders. */
const MuiSelectClearableContext = createContext<{ onClear: () => void; showClear: boolean }>({
    onClear: () => {},
    showClear: false,
});

type ClearIconInSelectProps = Pick<ComponentProps<typeof ClearIcon>, 'className' | 'style' | 'sx'> & {
    ownerState?: object;
};

function MuiSelectClearIcon({ className, style }: ClearIconInSelectProps) {
    const { onClear, showClear } = useContext(MuiSelectClearableContext);
    if (!showClear) {
        return <ArrowDropDownIcon className={className} style={style} />;
    }
    return (
        <IconButton
            size='small'
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClear();
            }}
        >
            <ClearIcon />
        </IconButton>
    );
}

export const MuiSelect = ({
    options,
    inputLabel,
    placeholder,
    formControlProps,
    formHelperText,
    formHelperTextProps,
    inputLabelProps,
    sx,
    clearable = false,
    onChange,
    name,
    value,
    ...props
}: {
    options: {
        label: string | JSX.Element;
        value: string;
        dataTestId?: string;
    }[];
    inputLabel?: string;
    placeholder?: string;
    formControlProps?: FormControlProps;
    formHelperTextProps?: FormHelperTextProps;
    formHelperText?: string;
    inputLabelProps?: InputLabelProps;
    clearable?: boolean;
} & SelectProps) => {
    const id = useId();

    const isEmpty =
        value === '' ||
        value === undefined ||
        value === null ||
        (Array.isArray(value) && (value as unknown[]).length === 0);

    // MUI's Select requires an array value when `multiple` is set; a scalar
    // empty string breaks it. Use the matching empty value per mode.
    const emptyValue = props.multiple ? [] : '';

    const showClear = Boolean(clearable && !isEmpty);

    const handleClear = useCallback(() => {
        const cleared = props.multiple ? [] : '';
        const event = {
            type: 'change' as const,
            target: { name: name ?? '', value: cleared },
            currentTarget: { name: name ?? '', value: cleared },
            preventDefault: () => {},
            stopPropagation: () => {},
        } as unknown as Parameters<NonNullable<SelectProps['onChange']>>[0];
        onChange?.(event, null);
    }, [onChange, name, props.multiple]);

    const clearableContextValue = useMemo(() => ({ onClear: handleClear, showClear }), [handleClear, showClear]);

    return (
        <FormControl {...formControlProps}>
            {inputLabel && (
                <InputLabel
                    id={id}
                    shrink={inputLabelProps?.shrink ?? (!!placeholder || undefined)}
                    {...inputLabelProps}
                    sx={{
                        fontSize: '14px !important',
                        ...inputLabelProps?.sx,
                        ...(props.size === 'small'
                            ? {
                                  top: '-4px',
                              }
                            : {}),
                    }}
                >
                    {inputLabel}
                </InputLabel>
            )}
            <MuiSelectClearableContext.Provider value={clearableContextValue}>
                <Select
                    displayEmpty={!!placeholder}
                    renderValue={(selected) => {
                        const isEmptySelected =
                            selected === undefined ||
                            selected === null ||
                            (Array.isArray(selected) && selected.length === 0) ||
                            selected === '';
                        if (isEmptySelected) {
                            if (placeholder) {
                                return (
                                    <Typography variant='subtitle2' color='text.disabled'>
                                        {placeholder}
                                    </Typography>
                                );
                            }
                            return null;
                        }
                        if (Array.isArray(selected)) {
                            return selected
                                .map((v) => {
                                    // biome-ignore lint/suspicious/noDoubleEquals: I need to compare numbers with string, and that works with objects too
                                    const opt = options.find((o) => o.value == v);
                                    return opt?.label ?? v;
                                })
                                .join(', ');
                        }
                        // biome-ignore lint/suspicious/noDoubleEquals: I need to compare numbers with string, and that works with objects too
                        const option = options.find((o) => o.value == selected);
                        return (option?.label ?? selected) as ReactNode;
                    }}
                    {...props}
                    onChange={onChange}
                    name={name}
                    value={isEmpty ? emptyValue : value}
                    label={inputLabel}
                    labelId={id}
                    IconComponent={clearable ? MuiSelectClearIcon : undefined}
                    sx={{
                        ...(!inputLabel || (isEmpty && !placeholder)
                            ? {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                      top: 0,
                                      '& legend': {
                                          display: 'none',
                                      },
                                  },
                              }
                            : {}),
                        ...sx,
                    }}
                >
                    {options.map(({ label, value, dataTestId }) => (
                        <MenuItem
                            key={typeof value === 'object' ? JSON.stringify(value) : value}
                            value={value}
                            data-testid={
                                dataTestId ||
                                `mui-select-item-${(props as { 'data-testid'?: string })['data-testid'] ?? ''}`
                            }
                        >
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            </MuiSelectClearableContext.Provider>
            {formHelperText && <FormHelperText {...formHelperTextProps}>{formHelperText}</FormHelperText>}
        </FormControl>
    );
};
